import { Get } from 'config/apiServiceConfig'
import AxiosInstance from 'config/apiServiceApiGateway'
import type { StaffMassUploadResponseType } from 'utils/apiList/massUpload'

export type FetchMPStaffQueryType =
  | Partial<{
      pageIndex: number
      pageSize: string
      locationType: string
      name: string
      locationId: number
      role: string
      staffId: string
    }>
  | undefined

export type FetchStaffRequestType = {
  location_ids?: number[]
  location_types?: string[]
  module?: {
    bank_account?: boolean
    department_location?: boolean
    staff_attributes?: boolean
    staff_vendor?: boolean
    termination_status?: boolean
    vehicle?: boolean
    general_data?: boolean
    personal_data?: boolean
    mp_tagging?: boolean
  }
  name?: string
  page_index?: number
  page_size?: number
  phone_number?: string
  roles?: string[]
  staff_ids?: string[]
  status_active?: string
}

export type MPStaffAddressType = {
  provinsi: string
  kota: string
  kecamatan: string
  kode_pos: string
  alamat: string
}

export type MPStaffGeneralDataType = {
  birth_date?: string
  education?: string
  marital_status?: string
  gender?: string
  religion?: string
  residential_address?: {
    alamat_sesuai_ktp?: boolean
    alamat_ktp?: Partial<MPStaffAddressType>
    alamat_domisili?: Partial<MPStaffAddressType>
  }
  golongan_darah?: string
  referral_code?: string
}

export type MPStaffPersonalDataType = {
  nama_ibu_kandung?: string
  no_kk?: string
  nomor_bpjs?: string
}

export type MPStaffTaggingType = {
  l3m_performance_tagging?: string
  violation_thorny_rules?: string
  overtime_availability?: string
  weekend_availability?: string
  shift_preference?: string
  skill_tags?: string[]
  feature_eligibility?: string[]
}

export type MPStaffType = {
  location_id: number
  join_date: string
  created_by: string
  location_name: string
  status: string
  role: string
  id: number
  bank_account_data: {
    id: number
    account_number: string
    account_name: string
    staff_id: number
    bank_name: string
  }
  bpjs_number?: string
  is_verified: boolean
  is_blacklist: boolean
  staff_termination_status_data: {
    termination_status_code: number
    approval_id: number
    termination_status: string
  }
  staff_attributes_data: {
    staff_id: number
    flexible_checkout: boolean
    working_location_id: number
    job_id: string
    hired_role: string
    tenure?: number
    tenure_segment?: string
    is_rehire?: boolean
    general_data?: MPStaffGeneralDataType
    personal_data?: MPStaffPersonalDataType
    mp_tagging?: MPStaffTaggingType
  }
  user_id: number
  staff_location_department_data: {
    staff_id: number
    location_type: string
    department: string
  }
  full_name: string
  staff_vendor_data: {
    staff_id: number
    vendor_id: number
    vendor_name: string
  }
  vehicle_type: string
  termination_date: string
  employment_type: string
  staff_role: {
    authority_role: string
    role_name_fmt: string
    active: boolean
  }[]
  is_active: boolean
  updated_by: string
  staff_app_version: string
  plate_number: string
  employee_detail: number
  vehicle_sub_type: string
  card_number: string
  termination_reason: string
  phone_number: string
  location_type: string
  tracker_id: string
}

export type MPStaffPaginationType = {
  direction: string
  number_of_elements: number
  page_index: number
  page_size: number
  sort: string
  total_elements: number
  total_pages: number
}

export type FetchStaffResponseType = {
  data: MPStaffType[]
  pagination: MPStaffPaginationType
}

export type GetResignationResonsTypeResponseType = {
  data: ResignationResonsType
}

export type ResignationResonsType = {
  [key: string]: {
    reasons: string[]
  }
}

export type PutResignationPayloadType = {
  approval_ids: number[]
  location_id: number
  requester_roles: string[]
  termination_date?: string
  termination_reason?: string
  join_date?: string
  updated_by: string
  is_reactivation_only?: boolean
  is_flexible_checkout?: boolean
}

export type FetchMPStaffVehicleTypeResponseType = {
  data: MPStaffVehicleType
}

export type MPStaffAPIResponseType = {
  data: {
    vehicle_sub_type: {
      [key: string]: string
    }[]
    vehicle_type: {
      [key: string]: string
    }[]
  }
}

export type MPStaffVehicleType = {
  vehicle_sub_type: {
    key: string
    value: string
  }[]
  vehicle_type: {
    key: string
    value: string
  }[]
}

export type FetchMPStaffVendorListResponseType = {
  data: MPStaffVendorListType[]
}

export type MPStaffVendorListType = {
  id: number
  name: string
  status: string
}

export type FetchMPStaffDepartementListResponseType = {
  data: MPStaffDepartementListType
}

export type MPStaffDepartementListType = {
  [key: string]: {
    departments: string[]
  }
}

export type FetchLocationListRequestType =
  | Partial<{
      name: string
      type: string
      pageSize: string
      active: boolean
    }>
  | undefined

export type FetchLocationListResponseType = {
  content: MPStaffLocationType[]
}

export type MPStaffLocationType = {
  location_active: boolean
  location_address: string
  location_detail: string
  location_flag: string
  location_id: number
  location_name: string
  location_type: string
  supervisor_names: string[]
}

export type MPStaffRequestPayloadType = Partial<{
  staff_location_id: number
  staff_card_number: string
  staff_plate_number: string
  staff_phone_number: string
  role: string[]
  staff_full_name: string
  employment_type: string
  location_type: string
  vehicle_type: string
  vehicle_sub_type: string | null
  tracker_id: string
  staff_vendor_id?: number | null
  bank_name: string
  account_name: string
  account_number: string
  join_date: string
  employee_detail: number
  department: string
  hired_role: string
  is_flexible_checkout: boolean
  job_id: string
  user_name: string
  termination_date?: string
  termination_reason?: string
  is_blacklist?: boolean
  approval_ids?: number[]
  staff_id?: number
}> & {
  general_data?: MPStaffGeneralDataType
  personal_data?: MPStaffPersonalDataType
  mp_tagging?: MPStaffTaggingType
}

export type PutMPStaffRequestType = {
  id: number
  data: MPStaffRequestPayloadType
}

export type LocationTypeListType = {
  id: number
  name: string
}

export type FetchJobIDsParamsType = {
  location_id: number
  employment_type: string
  role: string
}

export type JobIDsType = {
  id: number
  job_id: string
}

export const getMPStaffListAPI = (data: FetchStaffRequestType) =>
  AxiosInstance.Post<FetchStaffResponseType, true>({
    url: '/staff-management/internal/:version/staff/get-with-pagination',
    version: 'v1',
    data,
  })

export const putTerminationMPStaffAPI = (id: number, data: PutResignationPayloadType) =>
  AxiosInstance.Put({ url: `/staff-management/internal/v1/staff/${id}/resignation`, data })

export const putRejoinMPStaffAPI = (id: number, data: PutResignationPayloadType) =>
  AxiosInstance.Put({ url: `/staff-management/internal/v1/staff/${id}/rejoin`, data })

export const getVehicleTypeAPI = () =>
  AxiosInstance.Get<MPStaffAPIResponseType, true>({
    url: '/staff-management/internal/:version/staff-vehicles/types',
    version: 'v1',
  })

export const getEmploymentTypeAPI = () =>
  AxiosInstance.Get<{ data: string[] }, true>({
    url: '/staff-management/internal/:version/staff/employment-types',
    version: 'v1',
  })

export const getStaffVendorListAPI = () =>
  AxiosInstance.Get<FetchMPStaffVendorListResponseType, true>({
    url: '/staff-management/internal/:version/staff-vendors',
    version: 'v1',
  })

export const getDepartementListPI = () =>
  AxiosInstance.Get<FetchMPStaffDepartementListResponseType, true>({
    url: '/staff-management/internal/:version/staff/location-department',
    version: 'v1',
  })

export const getStaffRoleListAPI = (data: { locationType: string }) =>
  AxiosInstance.Post<{ data: string[] }, true>({
    url: '/staff-management/internal/:version/staff/roles-by-location-type',
    data,
    version: 'v1',
    convertRequest: true,
  })

export const createMPStaffAPI = (data: MPStaffRequestPayloadType) =>
  AxiosInstance.Post({
    url: `/staff-management/internal/:version/staff`,
    data,
    version: 'v1',
  })

export const putMPStaffAPI = ({ id, data }: PutMPStaffRequestType) =>
  AxiosInstance.Put({
    url: `/staff-management/internal/:version/staff/${id}`,
    data,
    version: 'v1',
    urlParams: { id },
  })

export const exportMPStaffAPI = ({
  location_type,
  location_id,
}: {
  location_type: string
  location_id?: number
}) =>
  AxiosInstance.Get<{ data: { file_url: string } }, true>({
    url: `/staff-management/internal/:version/staff/export`,
    params: { location_type, location_id },
    version: 'v1',
  })

export const postUpdateActiveRoleAPI = (staff_id: number, role: string, user_name: string) =>
  AxiosInstance.Post({
    url: `/staff-management/internal/:version/staff/change-active-role`,
    data: { staff_id, role, user_name },
    version: 'v1',
  })

export const getTerminationReasonsAPI = () =>
  AxiosInstance.Get<GetResignationResonsTypeResponseType, true>({
    url: '/staff-management/internal/:version/staff/termination-reason',
    version: 'v1',
  })

export const downloadMassUploadTemplateStaff = (params: { locationType: string }) =>
  AxiosInstance.Get<{ data: { file_url: string } }, true>({
    url: '/staff-management/internal/:version/staff/template',
    version: 'v1',
    params,
    convertRequest: true,
  })

export type BulkUploadStaffJobPayloadType = {
  job_type: string
  file_name: string
  file_content: string
}

export const JOB_TYPE_BULK_UPLOAD_STAFF = 'bulk_upload_staff'

export const postMassUploadStaff = (
  data: BulkUploadStaffJobPayloadType,
  config?: {
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  },
) =>
  AxiosInstance.Post<StaffMassUploadResponseType['data']>({
    url: '/staff-management/internal/:version/bulk-jobs/submit',
    version: 'v1',
    data,
    config,
  })

export const putMPStaffStatusAPI = (id: number, is_available: boolean) =>
  AxiosInstance.Put({
    url: `/staff-management/internal/v1/staff/${id}/availability`,
    data: {
      staff_id: id,
      is_available,
    },
  })

export const getLocationListAPI = (params: FetchLocationListRequestType) =>
  Get<FetchLocationListResponseType>({
    url: '/api/location',
    params,
  })

export const getJobIDsAPI = (params: FetchJobIDsParamsType) =>
  AxiosInstance.Get<{ data: JobIDsType[] }, true>({
    url: '/staff-management/internal/:version/hiring-funnel/job-id/list',
    version: 'v1',
    params,
  })

export const getLocationTypeAPI = () =>
  Get<{ data: LocationTypeListType[] }>({
    url: '/api/location/type',
  })
